import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Trash2, Edit, Tag, Loader2, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscountManager = () => {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', value: '', expires_at: '', active: true,
  });

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/discounts`, { headers });
      setDiscounts(res.data.discounts || []);
    } catch { toast.error('Failed to load discounts'); }
    finally { setLoading(false); }
  };

  const openForm = (discount = null) => {
    if (discount) {
      setEditingId(discount.id);
      setForm({
        code: discount.code || '',
        discount_type: discount.discount_type || 'percentage',
        value: discount.value || '',
        expires_at: discount.expires_at ? discount.expires_at.slice(0, 16) : '',
        active: discount.active !== false,
      });
    } else {
      setEditingId(null);
      setForm({ code: '', discount_type: 'percentage', value: '', expires_at: '', active: true });
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.value) {
      toast.error('Code and value are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
      };
      if (editingId) {
        await axios.put(`${API}/admin/discounts/${editingId}`, payload, { headers });
        toast.success('Discount updated');
      } else {
        await axios.post(`${API}/admin/discounts`, payload, { headers });
        toast.success('Discount created');
      }
      setShowForm(false);
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await axios.delete(`${API}/admin/discounts/${id}`, { headers });
      toast.success('Deleted');
      fetchDiscounts();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (discount) => {
    try {
      await axios.put(`${API}/admin/discounts/${discount.id}`, { active: !discount.active }, { headers });
      fetchDiscounts();
    } catch { toast.error('Failed to update'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No expiry';

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16" data-testid="discount-manager-page">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
            <h1 className="font-serif text-2xl sm:text-3xl font-light">Discount Codes</h1>
          </div>
          <Button onClick={() => openForm()} className="bg-accent hover:bg-accent/90" data-testid="add-discount-btn">
            <Plus size={14} className="mr-1" /> New Code
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="animate-spin mx-auto" /></div>
        ) : discounts.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-lg">
            <Tag size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-serif text-xl mb-2">No discount codes yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create your first coupon code.</p>
            <Button onClick={() => openForm()} variant="outline">Create Discount Code</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {discounts.map((d) => (
              <div key={d.id} className="bg-card border border-border rounded-lg p-4 flex items-center gap-4" data-testid="discount-row">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-lg font-semibold" data-testid="discount-code">{d.code}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-sans ${d.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {d.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="font-sans text-sm text-muted-foreground">
                    {d.discount_type === 'percentage' ? `${d.value}% off` : `₹${d.value} off`}
                    {' · '}Expires: {formatDate(d.expires_at)}
                    {' · '}Used {d.times_used || 0} times
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(d)} data-testid="discount-toggle-active"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${d.active ? 'bg-accent' : 'bg-muted'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${d.active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <Button variant="outline" size="sm" onClick={() => openForm(d)} data-testid="discount-edit-btn">
                    <Edit size={14} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(d.id)}
                    className="text-red-500 hover:bg-red-50" data-testid="discount-delete-btn">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="discount-form-modal">
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl font-light">{editingId ? 'Edit' : 'Create'} Discount Code</h2>
                <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Code</Label>
                  <Input data-testid="discount-code-input" value={form.code}
                    onChange={(e) => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. SUMMER20" className="font-mono uppercase" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <select value={form.discount_type} onChange={(e) => setForm(p => ({ ...p, discount_type: e.target.value }))}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-sm" data-testid="discount-type-select">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Value</Label>
                    <Input data-testid="discount-value-input" type="number" value={form.value}
                      onChange={(e) => setForm(p => ({ ...p, value: e.target.value }))}
                      placeholder={form.discount_type === 'percentage' ? '10' : '500'} />
                  </div>
                </div>
                <div>
                  <Label>Expires At</Label>
                  <Input type="datetime-local" data-testid="discount-expires-input" value={form.expires_at}
                    onChange={(e) => setForm(p => ({ ...p, expires_at: e.target.value }))} />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty for no expiry.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="discount-active" checked={form.active}
                    onChange={(e) => setForm(p => ({ ...p, active: e.target.checked }))} />
                  <Label htmlFor="discount-active" className="!mb-0">Active</Label>
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full bg-accent hover:bg-accent/90" data-testid="discount-save-btn">
                  {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
                  {editingId ? 'Update' : 'Create'} Discount
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountManager;
